import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { formatDateWithTime } from '../../../../../../common-utils/formatters';
import type { BlogPost } from '../../../../../@types/BlogPost';
import { BlogPostStatusEnum } from '../../../../../@types/BlogPost';
import type { RootState } from '../../../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../../../utils/JWTHeader';
import { ROUTES } from '../../../../../utils/routes';
import { showToast } from '../../../../common/Notification';
import Popup from '../../../../common/Popup/Popup.module';
import Spinner from '../../../../common/Spinner';

interface BlogPostSidebarProps {
  blogPost: BlogPost;
  onUpdate: (data: Partial<BlogPost> | FormData) => Promise<void>;
  isSaving: boolean;
  lastSaved: Date | null;
  onAIContentGenerated?: (content: { title: string; content: string }) => void;
  onAIContentAdjusted?: (content: string) => void;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: Date;
}

export default function BlogPostSidebar({
  blogPost,
  onUpdate,
  isSaving,
  lastSaved,
  onAIContentGenerated,
  onAIContentAdjusted,
}: BlogPostSidebarProps) {
  const [title, setTitle] = useState(blogPost.title);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [showAIChat, setShowAIChat] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showPixabayModal, setShowPixabayModal] = useState(false);
  const [pixabaySearchTerm, setPixabaySearchTerm] = useState('');
  const [pixabayImages, setPixabayImages] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSave = () => {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('content', blogPost.content || '');
    formData.append('status', blogPost.status);
    onUpdate(formData);
  };

  const handleStatusChange = (newStatus: BlogPostStatusEnum) => {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('content', blogPost.content || '');
    formData.append('status', newStatus);
    onUpdate(formData);
  };

  const handleTitleBlur = () => {
    if (title !== blogPost.title) {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', blogPost.content || '');
      formData.append('status', blogPost.status);
      onUpdate(formData);
    }
  };

  const handleImageChange = async(
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('title', title);
    formData.append('content', blogPost.content || '');
    formData.append('status', blogPost.status);
    formData.append('mainImageFile', file);

    onUpdate(formData);
  };

  const searchPixabayImages = async(query: string) => {
    if (!query.trim()) {
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://pixabay.com/api/?key=${process.env.REACT_APP_PIXABAY_API_KEY}&q=${encodeURIComponent(query)}&image_type=photo&per_page=30`,
      );
      const data = await response.json();
      setPixabayImages(data.hits || []);
    } catch (error) {
      console.error('Error fetching Pixabay images :', error);
      showToast(
        'error',
        "Une erreur est survenue lors de la recherche d'images.",
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handlePixabayImageSelect = async(imageUrl: string) => {
    try {
      // Convert Pixabay image URL to Blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Create a File object from the Blob
      const file = new File([blob], 'pixabay-image.jpg', {
        type: 'image/jpeg',
      });

      const formData = new FormData();
      // Only send required fields
      formData.append('title', title);
      formData.append('content', blogPost.content || '');
      formData.append('status', blogPost.status);
      formData.append('mainImageFile', file);

      await onUpdate(formData);
      setShowPixabayModal(false);
      showToast('success', 'Image ajoutée avec succès.');
    } catch (error) {
      console.error('Error processing Pixabay image :', error);
      showToast('error', "Une erreur est survenue lors de l'ajout de l'image.");
    }
  };

  const handleDelete = async() => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) {
      return;
    }

    try {
      const response = await fetch(`/api/blog-posts/${blogPost.id}`, {
        method: 'DELETE',
        ...JWTHeader(jwt),
      });

      if (!response.ok) {
        throw new Error('Failed to delete blog post');
      }

      showToast('success', 'Article supprimé.');
      navigate(ROUTES.ADMIN_BLOG_POSTS);
    } catch (error) {
      console.error('Failed to delete blog post :', error);
      showToast('error', 'Une erreur est survenue lors de la suppression.');
    }
  };

  const handleAIGeneration = async() => {
    if (!aiPrompt || !jwt) {
      showToast('error', 'Veuillez entrer un prompt pour la génération.');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch('/api/blog-posts/generate-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        body: JSON.stringify({
          prompt: aiPrompt,
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du contenu');
      }

      const data = await response.json();
      if (onAIContentGenerated) {
        onAIContentGenerated(data);
        setTitle(data.title);
      }
      showToast('success', 'Contenu généré avec succès.');
    } catch (error) {
      console.error('Erreur :', error);
      showToast('error', 'Erreur lors de la génération du contenu.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAIAdjustment = async() => {
    if (!prompt.trim() || !jwt) {
      return;
    }

    try {
      setIsGenerating(true);
      const response = await fetch('/api/blog-posts/adjust-content', {
        method: 'POST',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          currentContent: blogPost.content,
          chatHistory: messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to adjust content');
      }

      const data = await response.json();

      // Add messages to chat history with timestamps
      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: prompt,
          timestamp: new Date(),
        },
        {
          role: 'assistant',
          content: data.explanation,
          timestamp: new Date(),
        },
      ]);

      if (onAIContentAdjusted) {
        onAIContentAdjusted(data.content);
      }

      setPrompt('');
      showToast('success', 'Contenu ajusté avec succès.');
    } catch (error) {
      console.error('Failed to adjust content :', error);
      showToast('error', "Échec de l'ajustement du contenu");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleResetChat = () => {
    if (
      window.confirm(
        "Êtes-vous sûr de vouloir réinitialiser l'historique de la conversation ?",
      )
    ) {
      setMessages([]);
      setPrompt('');
      showToast('success', 'Historique de conversation réinitialisé');
    }
  };

  return (
    <div className="w-full overflow-y-auto rounded-lg bg-white p-2 lg:h-full lg:w-80">
      <div className="space-y-6 bg-rgpd-white p-4">
        <div>
          <h2 className="mb-2 text-lg font-semibold">
            Paramètres de l'article
          </h2>
          <div className="flex items-center text-sm text-gray-500">
            {isSaving ? (
              <div className="flex items-center">
                <Spinner size="sm" className="mr-2" />
                Sauvegarde en cours...
              </div>
            ) : (
              lastSaved && (
                <>
                  Dernière sauvegarde :{' '}
                  {formatDateWithTime(lastSaved.toISOString())}
                </>
              )
            )}
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className={
            `flex w-full items-center justify-center rounded-xl bg-rgpd-green px-2 py-2 text-white transition-colors hover:bg-rgpd-green-dark ${ 
            isSaving ? 'cursor-not-allowed opacity-50' : ''}`
          }
        >
          {isSaving ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Sauvegarde...
            </>
          ) : (
            'Sauvegarder'
          )}
        </button>

        <div className="space-y-2">
          <label className="text-sm font-medium">Titre</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            className="w-full rounded-xl border p-2"
            disabled={isSaving}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Statut</label>
          <select
            value={blogPost.status}
            onChange={(e) =>
              handleStatusChange(e.target.value as BlogPostStatusEnum)
            }
            className="w-full rounded-xl border p-2"
            disabled={isSaving}
          >
            <option value={BlogPostStatusEnum.DRAFT}>Brouillon</option>
            <option value={BlogPostStatusEnum.PUBLIC}>Public</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Image principale</label>
          <div className="flex flex-col space-y-2">
            {blogPost.mainImageName && (
              <img
                src={`/uploads/blog-posts/${blogPost.mainImageName}`}
                alt="Article"
                className="h-40 w-full rounded-lg object-cover"
              />
            )}
            <div className="flex gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 rounded-lg bg-gray-100 px-4 py-2 text-sm transition-colors hover:bg-gray-200"
              >
                Choisir un fichier
              </button>
              <button
                onClick={() => {
                  setShowPixabayModal(true);
                  setPixabaySearchTerm('');
                  setPixabayImages([]);
                }}
                className="flex flex-1 items-center justify-center rounded-lg bg-blue-100 px-4 py-2 text-sm transition-colors hover:bg-blue-200"
              >
                <PhotoIcon className="mr-2 h-5 w-5" />
                Pixabay
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleImageChange}
              accept="image/jpeg, image/png"
              className="hidden"
            />
          </div>
        </div>

        <div className="mb-4 space-y-2">
          <h3 className="mb-2 font-semibold">Génération IA</h3>
          <div className="space-y-2">
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Décrivez le contenu que vous souhaitez générer..."
              className="h-24 w-full rounded-xl border p-2"
              disabled={isGenerating}
            />
            <button
              onClick={handleAIGeneration}
              disabled={isGenerating || !aiPrompt}
              className={`flex w-full items-center justify-center rounded-xl bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 ${
                isGenerating ? 'cursor-not-allowed opacity-50' : ''
              }`}
            >
              {isGenerating ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <SparklesIcon className="mr-2 h-5 w-5" />
                  Générer le contenu
                </>
              )}
            </button>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowAIChat(!showAIChat)}
              className="flex flex-1 items-center gap-2 rounded-lg bg-gray-50 px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5" />
              {showAIChat
                ? "Masquer l'IA d'ajustement"
                : "Afficher l'IA d'ajustement"}
            </button>
            {showAIChat && messages.length > 0 && (
              <button
                onClick={handleResetChat}
                className="ml-2 rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                title="Réinitialiser la conversation"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {showAIChat && (
          <div className="mt-4 flex flex-1 flex-col">
            <div
              ref={messagesContainerRef}
              className="mb-4 max-h-[300px] flex-1 space-y-4 overflow-y-auto scroll-smooth"
            >
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'ml-8 bg-blue-100'
                      : 'mr-8 bg-gray-100'
                  }`}
                >
                  <p className="text-sm">{message.content}</p>
                  {message.timestamp && (
                    <p className="mt-1 text-xs text-gray-500">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Demandez à l'IA d'ajuster le contenu..."
                className="min-h-[3rem] flex-1 resize-y rounded-lg border p-2"
                disabled={isGenerating}
                rows={2}
              />
              <button
                onClick={handleAIAdjustment}
                disabled={isGenerating || !prompt.trim()}
                className={`rounded-lg p-2 ${
                  isGenerating || !prompt.trim()
                    ? 'cursor-not-allowed bg-gray-300'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        <div className="border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => navigate(ROUTES.ADMIN_BLOG_POSTS)}
            className="mb-2 w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Retour à la liste
          </button>
          <button
            className={`w-full rounded-xl bg-red-600 px-4 py-2 text-white hover:bg-red-700 ${
              isSaving ? 'cursor-not-allowed opacity-50' : ''
            }`}
            onClick={handleDelete}
            disabled={isSaving}
          >
            Supprimer l'article
          </button>
        </div>

        {/* Pixabay Modal */}
        <Popup
          isOpen={showPixabayModal}
          onClose={() => setShowPixabayModal(false)}
          title="Sélectionner une image Pixabay"
          width="800px"
        >
          <div className="p-4">
            <div className="mb-4">
              <input
                type="text"
                value={pixabaySearchTerm}
                onChange={(e) => setPixabaySearchTerm(e.target.value)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && searchPixabayImages(pixabaySearchTerm)
                }
                placeholder="Rechercher des images..."
                className="w-full rounded-lg border p-2"
              />
              <button
                onClick={() => searchPixabayImages(pixabaySearchTerm)}
                disabled={isSearching}
                className="mt-2 w-full rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 disabled:opacity-50"
              >
                {isSearching ? (
                  <Spinner size="sm" className="mx-auto" />
                ) : (
                  'Rechercher'
                )}
              </button>
            </div>

            <div className="grid max-h-[60vh] grid-cols-3 gap-4 overflow-y-auto">
              {pixabayImages.map((image) => (
                <div
                  key={image.id}
                  className="cursor-pointer transition-opacity hover:opacity-80"
                  onClick={() => handlePixabayImageSelect(image.largeImageURL)}
                >
                  <img
                    src={image.previewURL}
                    alt={image.tags}
                    className="aspect-square w-full rounded-lg object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </Popup>
      </div>
    </div>
  );
}
